import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, Clock, TrendingUp } from 'lucide-react';

interface ContentStats {
  total_modules: number;
  quality_content: number;
  short_content: number;
  placeholder_content: number;
  quality_percentage: number;
  active_operations: number;
  completed_today: number;
}

export default function ContentQualityMonitor() {
  const [stats, setStats] = useState<ContentStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_content_quality_stats');
      
      if (error) throw error;
      
      // Type-safe handling of JSONB response
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        setStats(data as unknown as ContentStats);
      }
    } catch (error) {
      console.error('Failed to fetch content stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-lg" />
          <div className="h-32 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  const qualityPercentage = stats?.quality_percentage || 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Content Quality Monitor</h1>
        <p className="text-muted-foreground">
          Real-time monitoring of course content quality across all modules
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Modules</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_modules || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all courses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Quality Content</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats?.quality_content || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              ≥ 200 words
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Short Content</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {stats?.short_content || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              10-199 words
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Placeholders</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats?.placeholder_content || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              &lt; 10 words
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Content Quality Score</CardTitle>
          <CardDescription>
            Percentage of modules with quality content (≥200 words)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Quality Score</span>
              <span className="font-bold">{qualityPercentage.toFixed(1)}%</span>
            </div>
            <Progress value={qualityPercentage} className="h-3" />
          </div>

          <div className="flex items-center gap-2 pt-2">
            {qualityPercentage >= 90 ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-600 font-semibold">Excellent Quality</span>
              </>
            ) : qualityPercentage >= 70 ? (
              <>
                <Clock className="w-5 h-5 text-yellow-600" />
                <span className="text-sm text-yellow-600 font-semibold">Good Quality</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-600 font-semibold">Needs Improvement</span>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Enhancement Operations</CardTitle>
          <CardDescription>Current background enhancement activity</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Active Operations</span>
            <Badge variant={stats?.active_operations ? 'default' : 'secondary'}>
              {stats?.active_operations || 0} running
            </Badge>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Completed Today</span>
            <Badge variant="outline">
              {stats?.completed_today || 0} modules
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
